/**
 * useRevealProposal - "Reveal X?" before a hidden figure's act reaches a player (plan map-display, G1; the card:
 * plan design-gaps, Z4).
 *
 * When the GM acts as a hidden figure on someone who is NOT hidden, the act
 * would reach a player, so the GM is asked first (ruling 2026-10-07):
 *   - Reveal X: the figure is revealed, THEN the act goes ahead in the open;
 *   - Keep hidden: the act goes ahead VEILED - the sync layer's veil names
 *     it "Unknown N" wherever a player could read it.
 * A visible actor, or a hidden one acting on another hidden figure, is never asked.
 *
 * The ask is a CARD over the map, not a dialog (the session page): the table does not stop for it, and a GM who
 * waits REVEAL_WAIT_MS has Keep hidden taken (ruled 2026-10-13: a GM who looks away must leak nothing; the design
 * had Reveal). One ask at a time: an act proposed while one is open is not made; and if
 * the GM picks another actor or target while it is up, the card closes unanswered: nothing revealed, nothing acts.
 *
 * @param {object} args - actor, activeTab (the target), targetName, hidden (useHiddenFigures: isHidden, aliasOf),
 *   reveal (id) => Promise
 * @returns {{ propose: (act: Function) => Promise<void>, revealAsk: object|null }} `propose` runs `act` once the GM
 *   has answered; `revealAsk` is the open card - who acts, on whom, under which alias, and `answer(choice)`
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long the card waits before Keep hidden is taken (the session page's four seconds). */
export const REVEAL_WAIT_MS = 4000;

export default function useRevealProposal({ actor, activeTab, targetName, hidden, reveal }) {
  const { isHidden, aliasOf } = hidden;
  const { revealAsk, ask, open } = useRevealCard();
  // The card does not stop the table: if the GM picks another pair while it is up, it closes unanswered - the act
  // reads the live attack, so answering it would act for another (the Track Z review).
  const card = useRef(null);
  card.current = revealAsk;
  const actorId = actor?.id;
  useEffect(() => {
    card.current?.answer(null);
  }, [actorId, activeTab]);
  const propose = useCallback(
    async (act) => {
      const asks = !!actor && isHidden(actor.id) && !!activeTab && !isHidden(activeTab);
      if (!asks) return act();
      if (open.current) return undefined;
      const answer = await ask({ actor: actor.name, alias: aliasOf(actor.id), target: targetName ?? null });
      if (!answer) return undefined;
      if (answer === 'reveal') await reveal(actor.id);
      return act();
    },
    [actor, activeTab, targetName, isHidden, aliasOf, reveal, ask, open],
  );
  return { propose, revealAsk };
}

/** The card held as state: `ask(card)` opens it and resolves with the answer; the wait answers Keep hidden. */
function useRevealCard() {
  const [revealAsk, setRevealAsk] = useState(null);
  const open = useRef(false);
  const ask = useCallback((card) => {
    open.current = true;
    return new Promise((resolve) => setRevealAsk({ ...card, answer: resolve })).finally(() => {
      open.current = false;
      setRevealAsk(null);
    });
  }, []);
  useEffect(() => {
    if (!revealAsk) return undefined;
    const t = setTimeout(() => revealAsk.answer('veiled'), REVEAL_WAIT_MS);
    return () => clearTimeout(t);
  }, [revealAsk]);
  return { revealAsk, ask, open };
}
